import { transient, autoinject, bindable, computedFrom } from "aurelia-framework";
import { GuidHelper } from "../../helpers/guid.helper";
import type { AttachmentFile } from "../../models/attachments.models";
import { Reflection } from "../../helpers/reflection";
import { NotificationClientService, NotificationType } from "../../services/basic/notification-client.service";

//Load styles
require("./comp-file-uploader.scss");

@transient()
@autoinject()
export class CompFileUploader {

    public fileInput: HTMLInputElement;
    public identifier: string = GuidHelper.generateGuid();

    @bindable()
    accept: string = "application/pdf,image/png,image/jpeg,image/jpg,application/zip,application/x-zip-compressed,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.ms-outlook,.msg";
  
    @bindable()
    extensions: string = ".JPG, .PNG, .PDF";
  
    //use to set the width of the attachment to 100%
    @bindable()
    fullWidthAttachment: boolean = false;

    //use to allow amount of files to be uploaded
    @bindable()
    amountFilesAllowed: number;

    @bindable()
    files: AttachmentFile[] = [];

    @bindable()
    showAttachmentTypes: boolean= false;

    @bindable()
    entityTypeId: number;

    @bindable()
    canDeleteFiles: boolean = true;

    @bindable()
    disabled: boolean = false;

    @bindable()
    allowMultiple: boolean = false;

    @bindable()
    showDragAndDrop: boolean = false;

    @bindable()
    maxFileSizeMb: number | string = 100;

    @bindable()
    validateFiles: boolean = false;

    constructor(private notificationService: NotificationClientService) {
    }

    public onFilesSeleced($event) {
        let files = $event.srcElement.files;

        if (this.validateFiles && files && this.checkFilesType(files)) {
            
            //Check file sizes
            for (let i = 0; i < files.length; i++) {
                const file = files[0];
                const filesize = ((file.size / 1024) / 1024);

                //If an uploaded file's size exceeds the maximum file size
                const maxFileSizeMb = this.maxFileSizeMb ? Number(this.maxFileSizeMb) : null;
                if (maxFileSizeMb && maxFileSizeMb > 0 && filesize > maxFileSizeMb) {
                    this.notificationService.show(`File size cannot be more than ${maxFileSizeMb}MB`, "", NotificationType.Error);
                    return;
                }
            }
        } 
        else {
            files = null;
            this.notificationService.show("Invalid file type", "", NotificationType.Error);
        }

        if(!this.files)
            this.files = [];

        if (files) {
            this.files.push(...files);
        }
        this.reset($event.srcElement);

        //Remove focus from button after a file is selected
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }  
    }

    private checkFilesType(files: FileList): boolean {
        let isValid = files?.length > 0;

        //if accept is not set no need to check files type
        if (!this.accept) return isValid;
        const types = this.accept.split(',').map(x => x.trim());

        if (!types || types.length == 0) return isValid;

        for (let i = 0; i < files.length; i++) {
            //this is for the .msg type as type property was empty for those
            if(!files[i].type) {
                const ext = "." + files[i].name.split('.').pop();
                isValid = isValid && types.indexOf(ext) > -1;
            } else {
                isValid = isValid && types.indexOf(files[i].type) > -1;
            }

            if (!isValid) break;
        }

        return isValid;
    }

    public reset(fileInput: HTMLInputElement) {
        if (fileInput) {
            fileInput.value = "";
        }
    }

    public removeFile(index: number): void {
        if (index > -1 && this.files) {
            this.files.splice(index, 1);
        }
    }

    public onClick($event) {
        $event.stopPropagation();
        $event.preventDefault();
        this.fileInput.click();
    }

    public onDragOver($event: DragEvent) {
        $event.preventDefault();
    }

    public onDragDrop($event: DragEvent) {
        const files  = $event.dataTransfer.files;
        //Check if amountFilesAllowed is defined and if the amount of files is less or equal than the allowed amount
        if (!this.amountFilesAllowed || (files.length <= (this.amountFilesAllowed - this.files.length))) {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                this.files.push(file as AttachmentFile);
            } 
        } 
    }

    @computedFrom(Reflection.getPropertyPath<CompFileUploader>(z => z.entityTypeId))
    get attachmentTypesParameters():string {   
        return `${this.entityTypeId ? this.entityTypeId : 'NULL' },false`
    }

}
